// Generated by GitHub Copilot
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import useEscapeKey from '#hooks/use-escape-key.ts'
import { cn } from '#core/utils/cn.ts'

export interface QrCodeProps {
  /** The string encoded into the QR code. */
  value: string
  /** Rendered size in pixels. Default `96`. */
  size?: number
  /** Extra class merged onto the wrapper element. */
  className?: string
  /** When true, clicking the QR opens a centered modal showing it large enough to scan. */
  enlargeable?: boolean
}

/** Size of the enlarged QR shown in the modal — comfortably scannable. */
const ENLARGED_SIZE = 288

/**
 * Dumb, reusable QR-code visual. Renders the given `value` as an SVG QR code and,
 * when `enlargeable`, lets the user click it to open a centered, scan-sized modal.
 * Knows nothing about the domain — it only takes a string.
 */
export function QrCode({ value, size = 96, className, enlargeable = false }: QrCodeProps) {
  const { t } = useTranslation('general')
  const [enlarged, setEnlarged] = useState(false)

  useEscapeKey(() => setEnlarged(false), enlarged)

  if (!enlargeable) {
    return (
      <span className={cn('d-inline-flex', className)}>
        <QRCodeSVG value={value} size={size} />
      </span>
    )
  }

  return (
    <>
      <button
        type="button"
        className={cn('btn', 'p-0', 'border-0', 'bg-transparent', 'lh-1', 'd-inline-flex', className)}
        onClick={() => setEnlarged(true)}
        aria-label={t('qr.enlarge')}
        title={t('qr.enlarge')}
      >
        <QRCodeSVG value={value} size={size} />
      </button>

      {enlarged && typeof document !== 'undefined' && createPortal(
        <div
          className="modal show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,.5)', zIndex: 1400 }}
          onClick={() => setEnlarged(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-sm" role="document">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="ynt-modal-centered-stack">
                <QRCodeSVG value={value} size={ENLARGED_SIZE} />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setEnlarged(false)}
                >
                  {t('shared.actions.close')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

export default QrCode
